import { useCallback, useRef } from "react";
import useSWR from "swr";
import { useAuth0 } from "@auth0/auth0-react";
import { aloApi, AloApiError } from "./api";

const REFRESH_MS = 30000;

export const useAloSummary = (enabled, from, to) => {
  const { data, error, mutate } = useSWR(
    enabled ? ["alo-orders/summary", from, to] : null,
    () => aloApi.summary(from, to),
    { refreshInterval: REFRESH_MS, shouldRetryOnError: false }
  );
  return { summary: data, error, isLoading: enabled && !data && !error, mutate };
};

export const useAloOrders = (enabled, query) => {
  const { from, to, status, q, page, size } = query;
  const { data, error, mutate } = useSWR(
    enabled ? ["alo-orders/list", from, to, status, q, page, size] : null,
    () => aloApi.listOrders(query),
    { refreshInterval: REFRESH_MS, shouldRetryOnError: false }
  );
  // Keep showing the last page while the next one loads, so the table doesn't flash empty.
  const last = useRef(undefined);
  if (data) last.current = data;
  return {
    orders: data || last.current,
    error,
    isLoading: enabled && !data && !error,
    isFirstLoad: enabled && !last.current && !error,
    mutate,
  };
};

/**
 * Wraps an API call so an expired Auth0 token is refreshed once and the call retried,
 * the same way the app layout refreshes the token on load.
 */
export const useWithFreshToken = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useCallback(
    async (call) => {
      try {
        return await call();
      } catch (error) {
        if (!(error instanceof AloApiError) || error.status !== 401) throw error;
        const token = await getAccessTokenSilently({ audience: "https://api.b2connect.me" });
        localStorage.setItem("auth0_token", token);
        return call();
      }
    },
    [getAccessTokenSilently]
  );
};
